<template>
  <tux-verification
    auto-submit
    show-cancel
    :channel="channel"
    :heading="heading"
    :issuer="issuerName"
    :manual-error-token.sync="error"
    :multi-channel="isMultichannel"
    :subheading="subheading"
    @cancel="onCancel"
    @show-options="continueAnotherWay"
    @submit="submit"
    @resend="resendOtp" />
</template>

<script>
import { AuthenticationStrategy } from '@mc-alberta/stark'
import { CustomLogger } from '@mc-alberta/supernova-scripts'
import { TuxVerification } from '@mc-alberta/tuxedo'
import { AuthenticationStatus } from '@mc-alberta/types'
import { defineComponent, getCurrentInstance, onMounted, ref } from '@vue/composition-api'
import { LOADERS, ROUTES } from '@/constants'
import { getI18nSingleton } from '@/i18n'
import router from '@/router'
import { getStark, sessionRouteGuard } from '@/store/modules/authentication'

export default defineComponent({
  name: ROUTES.VERIFY_ISSUER,
  components: { TuxVerification },
  beforeRouteEnter(to, from, next) {
    return sessionRouteGuard(AuthenticationStrategy.MDES, next)
  },
  setup() {
    onMounted(() => store.dispatch('stopAllLoaders'))
    const i18n = getI18nSingleton()
    const error = ref('')
    const stark = getStark()
    const { proxy } = getCurrentInstance()

    const store = proxy.$store
    const issuerName = store.getters.issuerName
    const isMultichannel = stark.authenticationMethods.length > 1
    const authenticationCredential =
      stark.authenticationMethod?.authenticationCredentialReference?.replace(/\*/g, '•')

    const channel = stark.authenticationMethod?.authenticationMethodType.replace('_OTP', '')

    const heading = i18n.t('authenticationVerify.heading')
    const subheading = i18n.t('authenticationVerify.subheading', {
      issuer: issuerName,
      credential: authenticationCredential
    })

    /**
     * Submits the OTP for validation.
     */
    async function submit(otpValue) {
      if (store.getters.isLoading) {
        // submit already inflight, log a 2nd attempt
        CustomLogger.error({
          event: 'MULTI_OTP_SUBMIT_ATTEMPT',
          error: new Error('Attempting to submit OTP multiple times.'),
          details: {
            routeName: this.$route.name,
            routePath: this.$route.path
          }
        })
        return
      }

      store.dispatch('startLoader', LOADERS.VERIFY_DETAILS)

      await stark.provideChallenge(otpValue)
      const session = await stark.validateSession()

      if (session.authenticationStatus === AuthenticationStatus.PENDING_CHALLENGE) {
        store.dispatch('stopLoader', LOADERS.VERIFY_DETAILS)
        error.value = i18n.t('authenticationVerify.error')
      } else {
        store.dispatch('handleAuthSession', session)
      }
    }

    /**
     * Resends the OTP (for a single-channel session).
     */
    function resendOtp() {
      store.dispatch('startLoader', LOADERS.RESEND_CODE)
      store.dispatch('handleAuthSession', stark.initializeSession())
    }

    /**
     * Routes to the credential selection page (for a multi-channel session).
     */
    function continueAnotherWay() {
      router.push({ name: ROUTES.VERIFY_OPTIONS })
    }

    /**
     * Shows the authentication session cancellation modal.
     */
    function onCancel() {
      this.$root.$emit('alcapone-toggle:al-modal', 'cancel-challenge-modal')
    }

    return {
      channel,
      heading,
      error,
      issuerName,
      isMultichannel,
      subheading,
      continueAnotherWay,
      submit,
      resendOtp,
      onCancel
    }
  },
  beforeDestroy() {
    this.$validator.pause()
  }
})
</script>
